import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import StudioCard from 'components/molecules/Landing/StudioCard';
import StudioDetailPanel from 'components/molecules/Landing/StudioDetailPanel';
import { STUDIO_CARDS_PER_ROW } from 'constants/aiStudio';
import { RGM_HOME_STUDIO, RGM_HOME_STUDIO_GRID } from 'constants/landingTestIds';
import useStudioGroups from 'hooks/landing/useStudioGroups';
import { NavScreenOption } from 'hooks/nav/useAccessibleNavScreens';
import { StudioGroup } from 'types/landing';
import { trackEventMixpanel } from 'utils/mixpanel';
import { LANDING_STUDIO_EXPAND, LANDING_STUDIO_NAVIGATE } from 'utils/mixpanelEventNames';

const chunk = <T,>(items: T[], size: number): T[][] =>
    items.reduce<T[][]>((rows, item, index) => {
        if (index % size === 0) rows.push([]);
        rows[rows.length - 1].push(item);
        return rows;
    }, []);

/**
 * The Studio grid — one card per accessible navigation group, expandable into its routes.
 *
 * Three columns is fixed rather than responsive: the panel's insertion point is defined in terms
 * of rows of three, so a breakpoint that reflowed the grid would drop the panel after the wrong
 * row. Narrow-viewport support means deriving the chunk size from a ResizeObserver and feeding it
 * to both the grid and the chunker, not adding breakpoint classes.
 */
function AiIntelligentStudio() {
    const navigate = useNavigate();
    const groups = useStudioGroups();
    const [openGroupKey, setOpenGroupKey] = useState<string | null>(null);

    const toggleGroup = (group: StudioGroup) => {
        const next = openGroupKey === group.key ? null : group.key;
        setOpenGroupKey(next);
        if (next) trackEventMixpanel(LANDING_STUDIO_EXPAND, { group: group.label });
    };

    const goToRoute = (group: StudioGroup, item: NavScreenOption) => {
        if (!item?.url) return;
        // Mirrors the GLOBAL_NAV_NAVIGATION payload so both navigation surfaces land in one report.
        trackEventMixpanel(LANDING_STUDIO_NAVIGATE, {
            screen: item.label,
            screenHeader: group.label,
            url: item.url,
        });
        navigate(item.url);
    };

    const openGroup = groups.find((group) => group.key === openGroupKey);

    return (
        <section data-testid={RGM_HOME_STUDIO} className="flex w-full flex-col gap-3">
            <p className="font-sm font-medium leading-[1.2] text-blackTitle">RGM PLANNING ENGINE</p>

            <div data-testid={RGM_HOME_STUDIO_GRID} className="flex w-full flex-col gap-3">
                {chunk(groups, STUDIO_CARDS_PER_ROW).map((row) => {
                    const rowKeys = row.map((group) => group.key);
                    const ownsPanel = Boolean(openGroupKey) && rowKeys.includes(openGroupKey as string);

                    return (
                        <React.Fragment key={rowKeys.join('|')}>
                            <div className="flex w-full items-stretch gap-4">
                                {row.map((group) => (
                                    <StudioCard
                                        key={group.key}
                                        group={group}
                                        isSelected={group.key === openGroupKey}
                                        isMuted={Boolean(openGroupKey) && group.key !== openGroupKey}
                                        onViewMore={toggleGroup}
                                    />
                                ))}
                                {/* Keep the last row's cards at one third width when it is short */}
                                {Array.from({ length: STUDIO_CARDS_PER_ROW - row.length }).map((_, index) => (
                                    <div key={`spacer-${index}`} className="min-w-px flex-[1_0_0]" aria-hidden />
                                ))}
                            </div>

                            {ownsPanel && openGroup && (
                                <StudioDetailPanel
                                    group={openGroup}
                                    onClose={() => setOpenGroupKey(null)}
                                    onSelect={goToRoute}
                                />
                            )}
                        </React.Fragment>
                    );
                })}
            </div>
        </section>
    );
}

export default AiIntelligentStudio;
